import { Card, CardBody, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { BiMenu } from 'react-icons/bi';
import { RiBook2Line } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { ResourceType, Transaction } from '../_openapi/portfolio-optimizer';
import { useTransactions } from '../api/transaction';
import { PageHeader } from '../widgets/PageHeader';
import { SegmentedControls } from '../widgets/SegmentedControls';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';
import { TableControls } from '../widgets/Table/TableControls';
import { usePersistentTableState } from '../widgets/Table/usePersistentTableState';
import { useTableColumnSettings } from '../widgets/Table/useTableColumnSettings';
import { transactionsListColumns } from './transactionsListColumns';

const columnHelper = createColumnHelper<Transaction>();
export const TransactionsList = () => {
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();

    const [searchParams, setSearchParams] = useSearchParams();
    const dataVariant = searchParams.get('dataVariant') || 'all';

    const { pagination, setPagination, sorting, setSorting, resetTable, columnFilters, setColumnFilters } =
        usePersistentTableState(transactionsListColumns, {
            sorting: [{ id: 'tradeDate', desc: true }],
        });

    const { data: transactionsPage, isFetching } = useTransactions({ pagination, columnFilters, sorting, dataVariant });

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } =
        useTableColumnSettings('transactions');

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => {
                    return (
                        <Menu isLazy>
                            <MenuButton
                                size="xs"
                                as={IconButton}
                                aria-label="Options"
                                icon={<Icon boxSize={3} as={BiMenu} />}
                                w="20px"
                                minW="20px"
                                h="20px"
                                variant="outline"
                                borderRadius="sm"
                            />
                            <Portal>
                                <MenuList>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiBook2Line} />}
                                        onClick={() => row.original.pId && viewPortfolio(row.original.pId)}
                                        isDisabled={!row.original.pId}
                                    >
                                        Portfolio details
                                    </MenuItem>
                                </MenuList>
                            </Portal>
                        </Menu>
                    );
                },
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            ...transactionsListColumns,
        ],
        [viewPortfolio],
    );

    const table = useReactTable({
        data: transactionsPage?.transactions || [],
        columns: columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        initialState: {
            columnPinning: {
                left: ['contextMenu'],
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <PageHeader title="Transactions">
                <Flex flex={1}>
                    <SegmentedControls
                        onClick={(value) =>
                            setSearchParams((params) => {
                                if (value === 'all') {
                                    params.delete('dataVariant');
                                } else {
                                    params.set('dataVariant', value);
                                }
                                return params;
                            })
                        }
                        selectedValue={dataVariant}
                        options={[
                            { value: 'all', label: 'Show All' },
                            { value: 'cash', label: 'Cash Movements' },
                            { value: 'transfers', label: 'Transfers' },
                        ]}
                    />
                </Flex>
            </PageHeader>
            <Card>
                <CardBody>
                    <TableControls table={table} onReset={resetTable} resourceType={ResourceType.TRANSACTION} />
                    <Table table={table} isLoading={isFetching} allowXOverflow />
                    <Pagination table={table} totalItems={transactionsPage?.totalNr} />
                </CardBody>
            </Card>
            <PortfolioDrawer {...portfolioDrawerProps} />
        </>
    );
};
